/**
 * Static content for the WS4 Session Report modal.
 *
 * Ported from `_legacy/ws4-assetlocker.js` lines 793-992 (`#reportModal`).
 * The legacy version was split across four tabs and the React port now
 * mirrors that — see `ReportModal.tsx` for the tab wiring. Each top-
 * level branch of `ReportContent` maps 1:1 to a tab pane:
 *   - `meta` + `kpis` + `executiveSummary` → Executive Summary
 *   - `outcomes` → Outcomes
 *   - `risks` → Risks & Issues
 *   - `nextSteps` → Next Steps
 * That structural mapping means no per-section `tabKey` discriminator
 * is needed; the modal simply picks the branch matching the active
 * tab.
 *
 * The accent token vocabulary matches `ws4-analysis/analysis-content.ts`
 * so both modals can share the same `tone()` helper if a future refactor
 * lifts it out.
 */

export type AccentToken =
  | "true-blue"
  | "sky"
  | "gold"
  | "green"
  | "melon"
  | "danger"
  | "muted";

export type MetaItem = {
  label: string;
  value: string;
  valueAccent?: AccentToken;
};

export type Kpi = {
  label: string;
  value: string;
  sub: string;
  accent: AccentToken;
};

export type Callout = {
  title: string;
  body: string;
  accent: AccentToken; // colour of left border + title
  // Lead glyph rendered before the title: ✓, ⚠, ℹ, etc.
  leadGlyph?: string;
};

export type Paragraph = {
  // We keep the body as plain text; bold-key terms in the legacy
  // are dropped to keep this content type a single primitive.
  // The React layer can do its own emphasis if needed in future.
  body: string;
};

export type Badge = {
  text: string;
  accent: AccentToken;
};

export type OutputRow = {
  output: string;
  description: string;
  status: Badge;
  owner: string;
};

export type DecisionRow = {
  title: string;
  body: string;
};

export type ReadinessRow = {
  label: string;
  delta: string;
  accent: AccentToken;
  pct: number;
  flag?: string;
};

export type RiskRow = {
  id: string;
  title: string;
  body: string;
  category: Badge;
  severity: string;
  severityAccent: AccentToken;
  likelihood: string;
  likelihoodAccent: AccentToken;
  owner: string;
  status: Badge;
};

export type ActionRow = {
  id: string;
  title: string;
  body: string;
  owner: string;
  ownerAccent: AccentToken;
  due: string;
};

export type ReportContent = {
  meta: MetaItem[];
  kpis: Kpi[];
  /** Right-aligned summary text rendered in the modal footer.
   *  Mirrors `_legacy/ws4-assetlocker.js` line 988:
   *  "DRAFT · 14 MAR 2026 · 14 ACTIONS · 6 RISKS". */
  footerSummary: string;
  executiveSummary: {
    purpose: Paragraph;
    callouts: Callout[];
    contributions: Paragraph;
  };
  outcomes: {
    outputs: OutputRow[];
    decisions: DecisionRow[];
    readiness: ReadinessRow[];
  };
  risks: RiskRow[];
  nextSteps: {
    immediate: ActionRow[];
    backlog: ActionRow[];
    nextWorkshopCallout: Callout;
  };
};

export const WS4_REPORT: ReportContent = {
  meta: [
    { label: "Workshop", value: "WS4 — Human-Agent Ways of Working" },
    { label: "Date", value: "14 March 2026" },
    { label: "Duration", value: "3h 28m" },
    { label: "Participants", value: "24 of 26 invited" },
    { label: "Facilitator", value: "Jordan K." },
    { label: "Exec Observer", value: "John C." },
    { label: "Status", value: "Draft · In Review", valueAccent: "gold" },
  ],
  footerSummary: "DRAFT · 14 MAR 2026 · 14 ACTIONS · 6 RISKS",
  kpis: [
    { label: "Completion Rate", value: "92%", sub: "All 3 activities completed", accent: "green" },
    { label: "Readiness Uplift", value: "+28%", sub: "Pre → post session delta", accent: "sky" },
    { label: "Actions Logged", value: "14", sub: "7 open · 3 in progress", accent: "gold" },
    { label: "Risk Flags", value: "3", sub: "2 critical · 1 medium", accent: "danger" },
  ],
  executiveSummary: {
    purpose: {
      body:
        "Workshop 4 — Human-Agent Ways of Working is the fourth session in the XAES Phase Zero programme. Its purpose was to establish shared mental models across all role clusters for how humans and AI agents collaborate in live operational settings. The session targeted three primary outcomes: agreement on a Role and Responsibility Map with explicit dual ownership fields; a draft Operating Mode Assignment Matrix aligned by domain and risk level; and a set of stress-tested Handoff Protocol principles ready for ratification.",
    },
    callouts: [
      {
        title: "Primary Objectives Met",
        body:
          "All three core outputs were produced. The Role and Responsibility Map reached consensus on dual ownership language, the Mode Assignment Matrix has been drafted and is ready for executive ratification, and the stress test scenarios surfaced critical protocol gaps that have been converted into assigned action items.",
        accent: "green",
        leadGlyph: "✓",
      },
      {
        title: "Governance Gap Remains Open",
        body:
          "Post-session readiness scores for the governance dimension remain below target at 3.2/5. Participants cannot yet reliably articulate the decision ownership model. A dedicated governance sprint (WS5 scoping) is required before deployment planning begins.",
        accent: "gold",
        leadGlyph: "⚠",
      },
      {
        title: "Activity 2.3 Ran Over Budget",
        body:
          "The Stress Test activity exceeded its 50-minute allocation by 10 minutes due to unresolved edge-case debate. Scenario 4 was not completed. The unresolved items have been converted to action log entries. Facilitation design for stress test activities should be tightened in future sessions.",
        accent: "true-blue",
        leadGlyph: "ℹ",
      },
    ],
    contributions: {
      body:
        "John C. introduced the distinction between execution ownership and accountability ownership — a framing now embedded in the charter language. John C. also coined the “mode graduation” concept, providing a maturity-gated pathway to greater agent autonomy that resolved the group's reluctance to assign Out-of-Loop modes. XAES-AI contributed three substantive AI inputs, including the identification of threshold drift as a top-3 deployment risk pattern and the proposal of randomised red-team injection as an automation bias countermeasure.",
    },
  },
  outcomes: {
    outputs: [
      {
        output: "Role & Responsibility Map",
        description:
          "RACI-style task map covering all four role clusters with separate “execution owner” and “accountability owner” fields per task",
        status: { text: "DRAFT", accent: "gold" },
        owner: "Jordan K.",
      },
      {
        output: "Mode Assignment Matrix",
        description:
          "Draft assignment of In/Above/On/Out-of-Loop modes by process domain, with context-dependent exception triggers and mode graduation criteria",
        status: { text: "IN REVIEW", accent: "gold" },
        owner: "John C.",
      },
      {
        output: "Stress Test Findings Log",
        description:
          "3 of 4 scenarios completed, with mitigations identified. Threshold governance rule, publish hold window, and red-team pilot proposal all actioned",
        status: { text: "COMPLETE", accent: "green" },
        owner: "XAES-AI",
      },
      {
        output: "Ways-of-Working Charter (Draft)",
        description:
          "Consolidated charter language incorporating dual ownership model, mode graduation policy, escalation triggers, and key design principles",
        status: { text: "DRAFT", accent: "gold" },
        owner: "Jordan K.",
      },
      {
        output: "Session Transcript",
        description:
          "Full auto-transcript with [ACTION] and [RISK FLAG] tags compiled by XAES-AI and distributed within 24 hours of session close",
        status: { text: "COMPLETE", accent: "green" },
        owner: "XAES-AI",
      },
    ],
    decisions: [
      {
        title: "Decision 1 — Dual Ownership Language",
        body:
          "All task records in the Role Map will carry two distinct owner fields: Execution Owner (may be agent or human) and Accountability Owner (always human). This distinction will be applied retroactively to existing process documentation.",
      },
      {
        title: "Decision 2 — Mode Graduation Policy",
        body:
          "Tasks may only graduate to Out-of-Loop operating mode after a minimum of 6 months of On-the-Loop operation without a critical incident. Graduation requires sign-off from both the relevant Ops Lead and the Governance Lead.",
      },
      {
        title: "Decision 3 — On-the-Loop as Default Mode",
        body:
          "All new agentic deployments will default to On-the-Loop configuration unless a documented rationale supports a different mode. In-the-Loop will be required for all compliance and regulatory sign-off processes.",
      },
      {
        title: "Decision 4 — Threshold Governance Rule",
        body:
          "All escalation confidence threshold changes must require dual sign-off from Ops Lead and Tech Lead and be permanently logged in the audit trail. No threshold may be changed unilaterally or informally.",
      },
    ],
    readiness: [
      { label: "Understanding of handoff triggers", delta: "2.7 → 4.1 (+52%)", accent: "green", pct: 82 },
      { label: "Trust in AI agent within guardrails", delta: "2.5 → 3.6 (+44%)", accent: "green", pct: 72 },
      { label: "Comfort working daily alongside agent", delta: "2.9 → 3.5 (+21%)", accent: "gold", pct: 70 },
      {
        label: "Understanding of governance model",
        delta: "2.1 → 3.2 (+52%)",
        accent: "gold",
        pct: 64,
        flag: "⚠ Below target",
      },
    ],
  },
  risks: [
    {
      id: "R-01",
      title: "Threshold Drift.",
      body:
        "Escalation confidence thresholds lowered over time to reduce alert noise — reducing the sensitivity of human oversight in On-the-Loop operations.",
      category: { text: "CRITICAL", accent: "danger" },
      severity: "High",
      severityAccent: "danger",
      likelihood: "High",
      likelihoodAccent: "danger",
      owner: "Ops Lead",
      status: { text: "MITIGATING", accent: "gold" },
    },
    {
      id: "R-02",
      title: "Governance Ambiguity.",
      body:
        "Participants cannot reliably articulate who owns an agent decision or what happens after an agentic error. Governance model post-session score: 3.2/5 — below the 4.0 threshold.",
      category: { text: "CRITICAL", accent: "danger" },
      severity: "High",
      severityAccent: "danger",
      likelihood: "Medium",
      likelihoodAccent: "gold",
      owner: "Governance Lead",
      status: { text: "OPEN", accent: "gold" },
    },
    {
      id: "R-03",
      title: "Automation Bias in Review Loops.",
      body:
        "Human reviewers in In-the-Loop processes may rubber-stamp agent outputs after repeated accurate results, creating a false assurance layer.",
      category: { text: "MEDIUM", accent: "gold" },
      severity: "Medium",
      severityAccent: "gold",
      likelihood: "Medium",
      likelihoodAccent: "gold",
      owner: "Ops Lead",
      status: { text: "PROPOSED: RED-TEAM PILOT", accent: "true-blue" },
    },
    {
      id: "R-04",
      title: "Agent Downtime in Live Operations.",
      body:
        "No agreed fallback procedure exists for agent unavailability during active operations. 3 participants flagged this in the residual concerns log.",
      category: { text: "MEDIUM", accent: "gold" },
      severity: "Medium",
      severityAccent: "gold",
      likelihood: "Low",
      likelihoodAccent: "gold",
      owner: "Ops Lead",
      status: { text: "IN PROGRESS", accent: "gold" },
    },
    {
      id: "R-05",
      title: "Reputational Risk — External Content.",
      body:
        "Agent-generated content scheduled for external channels may be published before human review in time-sensitive scenarios (e.g. breaking news event).",
      category: { text: "MEDIUM", accent: "gold" },
      severity: "High",
      severityAccent: "gold",
      likelihood: "Low",
      likelihoodAccent: "green",
      owner: "Change Lead",
      status: { text: "MITIGATING: PUBLISH HOLD", accent: "gold" },
    },
    {
      id: "R-06",
      title: "Role Boundary Ambiguity — On-the-Loop.",
      body:
        "Change & Comms cluster reported ambiguity in what “monitoring” means in practice. Risk of role confusion during live agent operations.",
      category: { text: "LOW", accent: "sky" },
      severity: "Low",
      severityAccent: "muted",
      likelihood: "Medium",
      likelihoodAccent: "gold",
      owner: "Change Lead",
      status: { text: "OPEN", accent: "gold" },
    },
  ],
  nextSteps: {
    immediate: [
      {
        id: "A-01",
        title: "Publish Decision Ownership Map (1-pager).",
        body:
          "A single-page visual that shows, for each task domain, who holds execution ownership and who holds accountability ownership. Mandatory reading before WS5.",
        owner: "Governance Lead",
        ownerAccent: "gold",
        due: "2 weeks",
      },
      {
        id: "A-02",
        title: "Ratify Mode Assignment Matrix with leadership.",
        body:
          "Present the draft matrix to the executive team for sign-off. Include the mode graduation policy and exception trigger logic.",
        owner: "John C.",
        ownerAccent: "green",
        due: "1 week",
      },
      {
        id: "A-03",
        title: "Implement threshold dual sign-off rule.",
        body:
          "Formalise the requirement that all confidence threshold changes require dual sign-off from Ops Lead and Tech Lead, and are permanently logged in the audit trail.",
        owner: "Ops Lead",
        ownerAccent: "gold",
        due: "1 week",
      },
      {
        id: "A-04",
        title: "Standardise Handoff Protocol Template.",
        body:
          "Distribute Group 2's structured template from Activity 2.3 as the baseline for all groups to ratify and adopt.",
        owner: "Ops Lead",
        ownerAccent: "gold",
        due: "2 weeks",
      },
      {
        id: "A-05",
        title: "Co-create Agent Communications Playbook.",
        body:
          "Work with Change & Comms cluster to define how agent involvement is communicated externally. Addresses role boundary ambiguity and narrative ownership risk.",
        owner: "Change Lead",
        ownerAccent: "sky",
        due: "3 weeks",
      },
    ],
    backlog: [
      {
        id: "B-01",
        title: "Agent Confidence Score — Operator UI.",
        body:
          "Add real-time confidence indicator to the operator-facing dashboard. Threshold bands should be defined by Tech Lead in consultation with Ops.",
        owner: "Tech Lead",
        ownerAccent: "true-blue",
        due: "Q3 Sprint",
      },
      {
        id: "B-02",
        title: "Red-Team Injection Pilot Proposal.",
        body:
          "Prepare a governance submission for the red-team injection programme. Include ethical disclosure framework, technical specification, and success criteria.",
        owner: "XAES-AI / Ops Lead",
        ownerAccent: "melon",
        due: "Q3 Sprint",
      },
      {
        id: "B-03",
        title: "News Signal Integration.",
        body:
          "Build a breaking-news signal detection layer that triggers automatic content review hold for agent-queued external posts. Aligns with John C.'s proposal in Activity 2.3.",
        owner: "Tech Lead",
        ownerAccent: "true-blue",
        due: "Q3–Q4",
      },
      {
        id: "B-04",
        title: "Stress Test Scenario 4 — Completion.",
        body:
          "The fourth stress test scenario (agent downtime during live operations) was not completed in WS4 due to time overrun. To be completed as a standalone exercise before WS5.",
        owner: "Jordan K.",
        ownerAccent: "gold",
        due: "Pre-WS5",
      },
    ],
    nextWorkshopCallout: {
      title: "WS5: Governance Sprint — Scheduled in 2 Weeks",
      body:
        "Workshop 5 will focus on governance model completion. Participants will walk through the Decision Ownership Map, define the incident response protocol for agentic errors, and agree the audit and compliance framework. Input from the Legal and Risk function will be required. Pre-read: Decision Ownership Map (A-01) and Mode Assignment Matrix (A-02) must be ratified before the session.",
      accent: "sky",
    },
  },
};

export function getReport(workshopId: string): ReportContent | null {
  if (workshopId === "WS4") return WS4_REPORT;
  return null;
}
